<template>
  <div class="mi-site mi-site--page">
    <a class="mi-skip" href="#main">Skip to content</a>
    <MarketingHeader />

    <main id="main" class="mi-contact">
      <div class="mi-hero-atmosphere" aria-hidden="true">
        <span class="mi-hero-orb mi-hero-orb-a"></span>
        <span class="mi-hero-orb mi-hero-orb-b"></span>
        <span class="mi-hero-orb mi-hero-orb-c"></span>
      </div>

      <div class="mi-shell mi-contact-grid">
        <div class="mi-contact-copy">
          <p class="mi-kicker">
            <span class="mi-kicker-dot" aria-hidden="true"></span>
            Talk to sales
          </p>
          <h1>
            Get MLS live on
            <span class="mi-hero-line">your <span class="mi-grad">domain</span>.</span>
          </h1>
          <p class="mi-lede mi-lede--left">
            Tell us about your brokerage. We reply within a business day.
          </p>

          <p class="mi-contact-direct">
            Or email
            <a :href="'mailto:' + contactEmail">{{ contactEmail }}</a>
          </p>
        </div>

        <div class="mi-contact-right">
          <aside class="mi-contact-aside">
            <p class="mi-kicker">
              <span class="mi-kicker-dot" aria-hidden="true"></span>
              August 24, 2026
            </p>
            <p class="mi-contact-aside-title">MLS Ingest is now a Brokurz company.</p>
            <p>
              Same network. New chapter.
              <router-link to="/announcement">Read the announcement</router-link>
            </p>
          </aside>

        <form class="mi-form" @submit.prevent="submit">
          <div v-if="sent" class="mi-form-done">
            <p class="mi-kicker">
              <span class="mi-kicker-dot" aria-hidden="true"></span>
              Request received
            </p>
            <h2>We’ll follow up at {{ sentTo }}.</h2>
            <p>Usually within one business day.</p>
          </div>
          <template v-else>
            <header class="mi-form-head">
              <h2>Talk to sales</h2>
              <p>Brokerage and what you need. We’ll take it from there.</p>
            </header>

            <div class="mi-form-row">
              <label>
                First name
                <input
                  v-model.trim="firstName"
                  type="text"
                  name="firstName"
                  autocomplete="given-name"
                  required
                />
              </label>
              <label>
                Last name
                <input
                  v-model.trim="lastName"
                  type="text"
                  name="lastName"
                  autocomplete="family-name"
                  required
                />
              </label>
            </div>

            <div class="mi-form-row">
              <label>
                Work email
                <input
                  v-model.trim="email"
                  type="email"
                  name="email"
                  autocomplete="email"
                  placeholder="you@brokerage.com"
                  required
                />
              </label>
              <label>
                Phone
                <input
                  v-model.trim="phone"
                  type="tel"
                  name="phone"
                  autocomplete="tel"
                  placeholder="Optional"
                />
              </label>
            </div>

            <label>
              Brokerage
              <input
                v-model.trim="company"
                type="text"
                name="organization"
                autocomplete="organization"
                required
              />
            </label>

            <div class="mi-form-row">
              <label>
                Role
                <select v-model="role" name="role" required>
                  <option value="" disabled>Select</option>
                  <option value="broker">Broker / owner</option>
                  <option value="ops">Operations</option>
                  <option value="team">Team lead</option>
                  <option value="tech">Vendor / tech</option>
                  <option value="other">Other</option>
                </select>
              </label>
              <label>
                What do you need?
                <select v-model="intent" name="intent" required>
                  <option value="" disabled>Select</option>
                  <option value="board">Get a board live</option>
                  <option value="licensing">Licensing / data agreement</option>
                  <option value="website">Brokurz website MLS</option>
                  <option value="operator">Operator / API access</option>
                  <option value="other">Something else</option>
                </select>
              </label>
            </div>

            <label>
              MLS board
              <input
                v-model.trim="board"
                type="text"
                name="board"
                placeholder="CRMLS, Stellar, Bright MLS…"
              />
            </label>

            <label>
              Anything else we should know?
              <textarea
                v-model.trim="message"
                name="message"
                rows="4"
                placeholder="Coverage, timeline, or a board that isn’t live yet."
              />
            </label>

            <p v-if="error" class="mi-form-error">{{ error }}</p>
            <button class="mi-btn" type="submit" :disabled="loading">
              {{ loading ? 'Sending…' : 'Talk to sales' }}
            </button>
            <p class="mi-form-fine">We’ll reply to your work email.</p>
          </template>
        </form>
        </div>
      </div>
    </main>

    <MarketingFooter />
  </div>
</template>

<script>
import MarketingHeader from './MarketingHeader'
import MarketingFooter from './MarketingFooter'
import { CONTACT_EMAIL } from '../lib/brand'
import '../assets/marketing.css'

const INTENT_LABELS = {
  board: 'Get a board live',
  licensing: 'Licensing / data agreement',
  website: 'Brokurz website MLS',
  operator: 'Operator / API access',
  other: 'Something else',
}

const ROLE_LABELS = {
  broker: 'Broker / owner',
  ops: 'Operations',
  team: 'Team lead',
  tech: 'Vendor / tech',
  other: 'Other',
}

export default {
  name: 'ContactPage',
  components: { MarketingHeader, MarketingFooter },
  data() {
    return {
      contactEmail: CONTACT_EMAIL,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      role: '',
      intent: '',
      board: '',
      message: '',
      loading: false,
      error: null,
      sent: false,
      sentTo: '',
    }
  },
  methods: {
    async submit() {
      this.loading = true
      this.error = null
      const name = [this.firstName, this.lastName].filter(Boolean).join(' ')
      const note = [
        this.intent ? 'Need: ' + (INTENT_LABELS[this.intent] || this.intent) : '',
        this.role ? 'Role: ' + (ROLE_LABELS[this.role] || this.role) : '',
        this.board ? 'Board: ' + this.board : '',
        this.phone ? 'Phone: ' + this.phone : '',
        this.message,
      ]
        .filter(Boolean)
        .join('\n')
      try {
        const res = await fetch('/api/public/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: this.firstName,
            lastName: this.lastName,
            name,
            email: this.email,
            company: this.company,
            message: note || 'Talk to sales',
            phone: this.phone || null,
            role: this.role || null,
            intent: this.intent || null,
            board: this.board || null,
          }),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || !data.ok) {
          throw new Error(data.error || 'Unable to send. Email us directly.')
        }
        this.sentTo = this.email
        this.sent = true
      } catch (err) {
        this.error = err.message || 'Unable to send.'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
